(() => {
  function parseFiniteInt(value) {
    const normalized = String(value ?? '').trim();
    if (!/^[+-]?\d+$/.test(normalized)) return null;
    const n = Number.parseInt(normalized, 10);
    return Number.isSafeInteger(n) ? n : null;
  }

  function parseDateParts(raw) {
    const match = String(raw || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return {
      year: parseInt(match[1], 10),
      month: parseInt(match[2], 10),
      day: parseInt(match[3], 10),
    };
  }

  function toIsoDate(parts) {
    if (!parts) return '';
    return `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
  }

  function normalizeIsoDate(raw) {
    return toIsoDate(parseDateParts(raw));
  }

  function calculateAgeOnDate(birthdateIso, referenceIso) {
    const birth = parseDateParts(birthdateIso);
    const ref = parseDateParts(referenceIso);
    if (!birth || !ref) return null;
    let age = ref.year - birth.year;
    if (ref.month < birth.month || (ref.month === birth.month && ref.day < birth.day)) age -= 1;
    return age >= 0 ? age : null;
  }

  function isMinorAge(age) {
    return Number.isFinite(age) && age >= 0 && age < 18;
  }

  function normalizePhoneForValidation(raw) {
    if (!raw) return '';
    const v = String(raw)
      .trim()
      .replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 65248))
      .replace(/[‐‑‒–—―ー−]/g, '-')
      .replace(/[()\s-]/g, '');
    if (v.startsWith('+81')) return `0${v.slice(3)}`;
    return v;
  }

  function isValidPhoneNumber(raw) {
    const normalized = normalizePhoneForValidation(raw);
    if (!normalized) return true;
    return /^0\d{9,10}$/.test(normalized);
  }

  function getTodayIsoDate() {
    const now = new Date();
    return toIsoDate({
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    });
  }

  function resolveAgeReferenceDate(raw, options = {}) {
    const normalized = normalizeIsoDate(raw);
    if (normalized) {
      return { value: normalized, source: 'configured' };
    }
    if (options.fallbackToToday) {
      return { value: getTodayIsoDate(), source: 'today' };
    }
    return { value: '', source: 'missing' };
  }

  function resolveMinorConsentReferenceDate(raw) {
    return resolveAgeReferenceDate(raw, { fallbackToToday: true });
  }

  function normalizeAgePricingVariants(variants) {
    const source = Array.isArray(variants) ? variants : [];
    return source
      .map((variant) => ({
        id: String(variant && variant.id ? variant.id : ''),
        title: String((variant && variant.title) || ''),
        available: !!(variant && variant.available),
        options:
          variant && Array.isArray(variant.options)
            ? variant.options.map((value) => String(value))
            : [],
        ageMin: parseFiniteInt(variant && variant.ageMin),
        ageMax: parseFiniteInt(variant && variant.ageMax),
      }))
      .filter((variant) => variant.id);
  }

  function getVariantLimit(variantLimits, variantId) {
    if (!variantId || !variantLimits || typeof variantLimits !== 'object') return null;
    return variantLimits[String(variantId)] || null;
  }

  function getInventoryMax(limit) {
    if (!limit) return null;
    const inventoryQuantity = parseFiniteInt(limit.inventoryQuantity);
    if (!Number.isFinite(inventoryQuantity)) return null;
    return Math.max(0, inventoryQuantity);
  }

  function getVariantMax({ variantId, variantLimits }) {
    const limit = getVariantLimit(variantLimits, variantId);
    let max = parseFiniteInt(limit && limit.quantityMax);
    const inventoryMax = getInventoryMax(limit);
    if (Number.isFinite(inventoryMax)) {
      max = Number.isFinite(max) ? Math.min(max, inventoryMax) : inventoryMax;
    }
    return Number.isFinite(max) ? max : null;
  }

  function getEffectiveMax({ platformMax, variantId, variantLimits }) {
    const variantMax = getVariantMax({ variantId, variantLimits });
    if (Number.isFinite(platformMax) && Number.isFinite(variantMax)) {
      return Math.min(platformMax, variantMax);
    }
    return Number.isFinite(platformMax) ? platformMax : variantMax;
  }

  function clampTourQuantity({ quantity, effectiveMax } = {}) {
    if (Number.isFinite(effectiveMax) && effectiveMax <= 0) return 0;
    if (!Number.isFinite(quantity)) return 1;
    if (quantity < 1) return 1;
    if (Number.isFinite(effectiveMax) && quantity > effectiveMax) return effectiveMax;
    return quantity;
  }

  function getAttendeeCountFromQuantity({ quantity, effectiveMax, isAgePricingTour } = {}) {
    if (isAgePricingTour) return 0;
    const clampedQuantity = clampTourQuantity({ quantity, effectiveMax });
    return Math.max(0, clampedQuantity - 1);
  }

  function getQuantitySyncPlan({
    rawQuantity,
    effectiveMax,
    lastRenderedCount,
    limitWarningActive,
    hasQuantityInput,
    isAgePricingTour,
  } = {}) {
    const safeRawQuantity = Number.isFinite(rawQuantity) ? rawQuantity : 1;
    const baseQuantity = hasQuantityInput ? safeRawQuantity : 1;
    const clampedQuantity = clampTourQuantity({ quantity: baseQuantity, effectiveMax });
    const nextCount = getAttendeeCountFromQuantity({
      quantity: clampedQuantity,
      effectiveMax,
      isAgePricingTour,
    });

    let shouldShowLimitWarning = false;
    let shouldHideLimitWarning = false;
    if (hasQuantityInput && Number.isFinite(effectiveMax)) {
      if (safeRawQuantity > clampedQuantity) {
        shouldShowLimitWarning = true;
      } else if (limitWarningActive && clampedQuantity < effectiveMax) {
        shouldHideLimitWarning = true;
      }
    } else if (limitWarningActive) {
      shouldHideLimitWarning = true;
    }

    return {
      safeRawQuantity,
      clampedQuantity,
      nextCount,
      shouldRender: nextCount !== lastRenderedCount,
      shouldWriteQuantity: !!hasQuantityInput && safeRawQuantity !== clampedQuantity,
      shouldShowLimitWarning,
      shouldHideLimitWarning,
    };
  }

  function hasMinorParticipant({ applicantAge, attendeeAges }) {
    if (isMinorAge(applicantAge)) return true;
    const normalizedAges = Array.isArray(attendeeAges) ? attendeeAges : [];
    return normalizedAges.some((age) => isMinorAge(age));
  }

  function normalizeAttendeeAges(attendeeAges) {
    const source = Array.isArray(attendeeAges) ? attendeeAges : [];
    return source.filter((age) => Number.isFinite(age) && age >= 0);
  }

  function getMinorConsentRequirement({ applicantBirthdateIso, referenceDateIso, attendeeAges } = {}) {
    const applicantAge =
      applicantBirthdateIso && referenceDateIso
        ? calculateAgeOnDate(applicantBirthdateIso, referenceDateIso)
        : null;
    const normalizedAttendeeAges = normalizeAttendeeAges(attendeeAges);

    return {
      applicantAge,
      attendeeAges: normalizedAttendeeAges,
      requiresConsent: hasMinorParticipant({
        applicantAge,
        attendeeAges: normalizedAttendeeAges,
      }),
    };
  }

  function findAgePricingVariant(agePricingVariants, ageValue) {
    const matches = normalizeAgePricingVariants(agePricingVariants).filter((variant) => {
      if (!Number.isFinite(variant.ageMin)) return false;
      if (ageValue < variant.ageMin) return false;
      if (Number.isFinite(variant.ageMax) && ageValue > variant.ageMax) return false;
      return true;
    });
    if (matches.length === 0) return { state: 'no_match', variant: null };
    if (matches.length > 1) return { state: 'duplicate', variant: null };
    return { state: 'matched', variant: matches[0] };
  }

  function createAgePricingEvaluation(state, message = '', matchedVariant = null, ageValue = null) {
    return { state, message, matchedVariant, ageValue };
  }

  function evaluateAgePricing({
    applicantBirthdateIso,
    referenceDateIso,
    agePricingVariants,
    currentVariantId,
  } = {}) {
    if (!applicantBirthdateIso) return createAgePricingEvaluation('pending');
    if (!referenceDateIso) {
      return createAgePricingEvaluation('missing_reference_date', 'ツアー日付が設定されていません。');
    }

    const ageValue = calculateAgeOnDate(applicantBirthdateIso, referenceDateIso);
    if (!Number.isFinite(ageValue)) {
      return createAgePricingEvaluation('invalid_birthdate', '生年月日を正しく入力してください。');
    }

    const match = findAgePricingVariant(agePricingVariants, ageValue);
    if (match.state === 'no_match') {
      return createAgePricingEvaluation('no_match', '該当する価格区分が見つかりません。', null, ageValue);
    }
    if (match.state === 'duplicate') {
      return createAgePricingEvaluation('duplicate', '価格区分の設定が重複しています。', null, ageValue);
    }

    if (String(currentVariantId || '') !== String(match.variant.id || '')) {
      return createAgePricingEvaluation('syncing', '価格区分を更新しています。', match.variant, ageValue);
    }

    if (!match.variant.available) {
      return createAgePricingEvaluation(
        'unavailable',
        '選択された価格区分は現在申し込めません。',
        match.variant,
        ageValue
      );
    }

    return createAgePricingEvaluation('ok', '', match.variant, ageValue);
  }

  function getAgePricingDisplayState({ state, bandValue, hasInventoryText } = {}) {
    const normalizedBandValue = String(bandValue || '').trim();
    const shouldShowPrice = state === 'ok';
    const shouldShowBand = shouldShowPrice && !!normalizedBandValue;
    const shouldShowInventory = shouldShowPrice && !!hasInventoryText;

    return {
      normalizedBandValue,
      shouldShowPrice,
      shouldShowBand,
      shouldShowInventory,
    };
  }

  function getAgePricingEvaluationPlan(evaluation = {}) {
    const normalizedAgeValue = Number.isFinite(evaluation.ageValue) ? evaluation.ageValue : null;
    const matchedVariant = evaluation.matchedVariant || null;
    const baseApplication = {
      state: String(evaluation.state || ''),
      message: String(evaluation.message || ''),
      matchedVariant,
      ageValue: normalizedAgeValue,
    };
    const shouldSyncVariant = baseApplication.state === 'syncing' && !!matchedVariant;

    return {
      baseApplication,
      shouldSyncVariant,
      syncFailureApplication: shouldSyncVariant
        ? {
            state: 'variant_sync_failed',
            message: '価格区分の更新に失敗しました。',
            matchedVariant,
            ageValue: normalizedAgeValue,
          }
        : null,
    };
  }

  function getAgePricingVariantSyncPlan({
    pendingVariantId,
    targetVariantId,
    optionsAlreadySelected,
    changedInputCount,
  } = {}) {
    const requestedVariantId = String(targetVariantId || '');
    const alreadyPending =
      String(pendingVariantId || '') === requestedVariantId && !!optionsAlreadySelected;

    return {
      requestedVariantId,
      shouldSync: !alreadyPending && !!requestedVariantId,
      shouldDispatchChange: !alreadyPending && Number(changedInputCount || 0) > 0,
    };
  }

  function resolveAgePricingVariantSync({ pendingVariantId, currentVariantId } = {}) {
    const normalizedPendingVariantId = String(pendingVariantId || '');
    const normalizedCurrentVariantId = String(currentVariantId || '');
    const syncCompleted =
      !!normalizedPendingVariantId && normalizedPendingVariantId === normalizedCurrentVariantId;
    const nextPendingVariantId = syncCompleted ? '' : normalizedPendingVariantId;
    const syncing = !!nextPendingVariantId;

    return {
      pendingVariantId: nextPendingVariantId,
      syncCompleted,
      shouldReevaluate: !syncing,
      syncing,
    };
  }

  function createAgePricingContractState({ state, matchedVariant, ageValue, referenceDate } = {}) {
    return {
      state: String(state || ''),
      expectedVariantId: matchedVariant ? String(matchedVariant.id || '') : '',
      ageValue: Number.isFinite(ageValue) ? String(ageValue) : '',
      bandValue: matchedVariant ? String(matchedVariant.title || '') : '',
      referenceDate: String(referenceDate || ''),
    };
  }

  function createAttendeeFieldValidation(fieldName, ok, message = '', inlineMessage = '') {
    return {
      ok: !!ok,
      fieldName: String(fieldName || ''),
      message: String(message || ''),
      inlineMessage: String(inlineMessage || ''),
    };
  }

  function getAttendeeFieldValidation({ fieldName, value, attendeeNumber } = {}) {
    const normalizedFieldName = String(fieldName || '');
    const normalizedValue = String(value ?? '').trim();
    const attendeeLabel =
      Number.isFinite(attendeeNumber) && attendeeNumber > 0 ? `同行者${attendeeNumber} の` : '同行者の';

    if (normalizedFieldName === 'name') {
      if (normalizedValue) return createAttendeeFieldValidation('name', true);
      return createAttendeeFieldValidation(
        'name',
        false,
        `${attendeeLabel}氏名を入力してください。`,
        '氏名を入力してください。'
      );
    }

    if (normalizedFieldName === 'age') {
      if (!normalizedValue) {
        return createAttendeeFieldValidation(
          'age',
          false,
          `${attendeeLabel}年齢を入力してください。`,
          '年齢を入力してください。'
        );
      }
      const age = parseFiniteInt(normalizedValue);
      if (Number.isFinite(age) && age >= 0) return createAttendeeFieldValidation('age', true);
      return createAttendeeFieldValidation(
        'age',
        false,
        `${attendeeLabel}年齢を正しく入力してください。`,
        '年齢を正しく入力してください。'
      );
    }

    if (normalizedFieldName === 'tel') {
      if (!normalizedValue || isValidPhoneNumber(normalizedValue)) {
        return createAttendeeFieldValidation('tel', true);
      }
      return createAttendeeFieldValidation(
        'tel',
        false,
        `${attendeeLabel}電話番号を正しく入力してください。`,
        '電話番号を正しく入力してください（例: 090-1234-5678）。'
      );
    }

    return createAttendeeFieldValidation(normalizedFieldName, true);
  }

  function getAttendeeRowFieldValidations({ attendee, attendeeNumber } = {}) {
    const attendeeState = attendee && typeof attendee === 'object' ? attendee : {};
    return {
      name: getAttendeeFieldValidation({
        fieldName: 'name',
        value: attendeeState.name,
        attendeeNumber,
      }),
      age: getAttendeeFieldValidation({
        fieldName: 'age',
        value: attendeeState.age,
        attendeeNumber,
      }),
      tel: getAttendeeFieldValidation({
        fieldName: 'tel',
        value: attendeeState.tel,
        attendeeNumber,
      }),
    };
  }

  function createValidationResult(section, ok, message = '', inlineErrorShown = false) {
    return {
      section: String(section || ''),
      ok: !!ok,
      message: String(message || ''),
      inlineErrorShown: !!inlineErrorShown,
    };
  }

  function createBirthdateValidationResult({ hasBirthdate } = {}) {
    if (hasBirthdate) return createValidationResult('birthdate', true);
    return createValidationResult('birthdate', false, '申込者（代表者）の生年月日を入力してください。');
  }

  function createAgePricingValidationResult({ isAgePricingTour, agePricingState, message } = {}) {
    if (!isAgePricingTour || String(agePricingState || '') === 'ok') {
      return createValidationResult('agePricing', true);
    }
    return createValidationResult(
      'agePricing',
      false,
      String(message || '価格区分を確定できません。')
    );
  }

  function createAttendeeSectionValidationResult({ isAgePricingTour, rowResults } = {}) {
    if (isAgePricingTour) return createValidationResult('attendees', true);
    const firstFailure = findFirstFailedValidationResult(rowResults);
    if (!firstFailure) return createValidationResult('attendees', true);
    return createValidationResult(
      'attendees',
      false,
      String(firstFailure.msg || firstFailure.message || ''),
      !!firstFailure.inlineErrorShown
    );
  }

  function findFirstFailedValidationResult(results) {
    const normalizedResults = Array.isArray(results) ? results : [];
    return normalizedResults.find((result) => result && !result.ok) || null;
  }

  function createTourValidationStateFallback({
    minorConsentRequired,
    agePricingState,
    expectedVariantId,
    results,
  } = {}) {
    const state = {
      birthdateOk: true,
      agePricingOk: true,
      attendeesOk: true,
      minorConsentRequired: !!minorConsentRequired,
      inlineErrorShown: false,
      message: '',
      agePricingState: String(agePricingState || ''),
      expectedVariantId: String(expectedVariantId || ''),
    };
    const firstFailure = findFirstFailedValidationResult(results);
    if (!firstFailure) return state;

    const flagKeyBySection = {
      birthdate: 'birthdateOk',
      agePricing: 'agePricingOk',
      attendees: 'attendeesOk',
    };
    const flagKey = flagKeyBySection[firstFailure.section];
    if (flagKey) state[flagKey] = false;
    state.inlineErrorShown = !!firstFailure.inlineErrorShown;
    state.message = String(firstFailure.message || '');
    return state;
  }

  function createTourValidationState(input = {}) {
    return createTourValidationStateFallback(input);
  }

  const api = {
    parseFiniteInt,
    parseDateParts,
    normalizeIsoDate,
    calculateAgeOnDate,
    isMinorAge,
    normalizePhoneForValidation,
    isValidPhoneNumber,
    getTodayIsoDate,
    resolveAgeReferenceDate,
    resolveMinorConsentReferenceDate,
    normalizeAgePricingVariants,
    getVariantLimit,
    getInventoryMax,
    getVariantMax,
    getEffectiveMax,
    clampTourQuantity,
    getAttendeeCountFromQuantity,
    getQuantitySyncPlan,
    hasMinorParticipant,
    getMinorConsentRequirement,
    findAgePricingVariant,
    evaluateAgePricing,
    getAgePricingDisplayState,
    getAgePricingEvaluationPlan,
    getAgePricingVariantSyncPlan,
    resolveAgePricingVariantSync,
    createAgePricingContractState,
    createAttendeeFieldValidation,
    getAttendeeFieldValidation,
    getAttendeeRowFieldValidations,
    createValidationResult,
    createBirthdateValidationResult,
    createAgePricingValidationResult,
    createAttendeeSectionValidationResult,
    findFirstFailedValidationResult,
    createTourValidationStateFallback,
    createTourValidationState,
  };

  if (typeof window !== 'undefined') {
    window.MFTourFormHelpers = Object.assign({}, window.MFTourFormHelpers || {}, api);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})();
